import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./_utils";

const iconButtonVariants = cva(
  "inline-flex items-center justify-center rounded-[var(--radius-control)] transition-colors " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus " +
    "disabled:opacity-50 disabled:pointer-events-none",
  {
    variants: {
      variant: {
        // Print surface; hover is a white bump, never a color shift.
        default:
          "bg-[var(--print-bg)] text-text-primary border border-[var(--print-border)] hover:bg-[rgba(var(--glass-rgb),0.7)]",
        ghost: "bg-transparent text-text-primary hover:bg-[rgba(var(--glass-rgb),0.6)]",
      },
      size: {
        sm: "h-9 w-9 md:h-6 md:w-6",
        md: "h-10 w-10 md:h-8 md:w-8",
      },
    },
    defaultVariants: { variant: "default", size: "md" },
  }
);

type IconButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof iconButtonVariants>;

/**
 * Square icon-only button. Requires `aria-label` for accessibility.
 *
 * @example
 * <IconButton aria-label="Close"><XIcon /></IconButton>
 * <IconButton variant="ghost" size="sm" aria-label="Edit"><PencilIcon /></IconButton>
 */
export function IconButton({
  className,
  variant,
  size,
  ...props
}: IconButtonProps) {
  return (
    <button
      className={cn(iconButtonVariants({ variant, size }), className)}
      {...props}
    />
  );
}
